import * as React from 'react'
import type { Clubhouse } from 'codegen/types'
import { useTranslations } from 'next-intl'
import { Link } from '@repo/web-ui/components/link'
import { tw } from 'utils/classnames'
import { AppRoutes } from '@repo/web-utils/app-routes'
import Image from 'next/image'

import { formatString } from '../_utils/format-string'
import { useClubhouseOnlineCount } from '../_hooks/use-clubhouse-online-count'
import { LoadingIcon } from '../assets/icons'
import { UserAvatar } from '../[slug]/_components/top-banner/_components/user-avatar'

export function ClubhouseCard({ clubhouse }: { clubhouse: Clubhouse }) {
  const t = useTranslations('pages.clubhouse')
  const onlineCount = useClubhouseOnlineCount(clubhouse.slug)

  return (
    <Link
      href={`${AppRoutes.CLUBHOUSE}${clubhouse.slug}`}
      className="bg-cf-white tablet:max-w-[396px] relative flex w-full justify-between gap-4 overflow-hidden rounded-[12px] border border-[#0A0A0B1A] p-5 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.10)] hover:border-[#0a0a0b38] hover:shadow-[0px_1px_3px_1px_#00000026,0px_1px_2px_0px_#0000004D] active:border-[#0A0A0B1A] active:shadow-none"
      key={clubhouse.name}
      data-testid={`clubhouse-card-${clubhouse.slug}`}
    >
      <div>
        <p className="text-20 font-semibold">{clubhouse.name}</p>
        <p className="text-cf-sf-gray-1150 text-12">{formatString(clubhouse.type)}</p>
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2 pt-3">
          <div className="flex flex-row items-center">
            <div className="bg-cf-cricut-green relative mr-1.5 flex size-2 self-center rounded-full">
              <div className="bg-cf-cricut-green absolute flex size-2 animate-ping self-center rounded-full" />
            </div>
            <div className="flex flex-row">
              {onlineCount ? (
                <>
                  <span className="text-14 mr-1 font-medium">{onlineCount}</span>
                  <p className="text-14 font-medium">{t('online')}</p>
                </>
              ) : (
                <div className="flex items-center">
                  <LoadingIcon className="mr-1 size-3 animate-spin" />
                  <p className="text-14 font-medium text-transparent">{t('online')}</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div className="mt-1 flex flex-row">
              <UserAvatar size="small" displayCount={3} slug={clubhouse.slug} />
            </div>
            <p className="text-12 font-semibold leading-4">
              {clubhouse.membersTotal + ' ' + t('members')}
            </p>
          </div>
        </div>
      </div>
      <div
        className={tw(
          'flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-full',
          clubhouse.bgColor ? `bg-[${clubhouse?.bgColor}]` : 'bg-cf-white',
        )}
      >
        <Image src={clubhouse?.avatarImage || ''} alt="logo" width={66} height={66} />
      </div>
    </Link>
  )
}
